import Media from "@/types/Media.interface"

export interface SearchPayload {
    searchtext: string      //  The actual search query we are finding media based on
    folder?: string         //  An optional param specifiying which folder the search is in
}

interface State {
    media: Media[]          //  Contains our media
    storageLimit: number    //  The number of bytes in storage we have as the upper limit
    storageUsed: number     //  The number of bytes in storage we have currently used
}

const defaults = () => {
    return {
        media: [],
        storageLimit: 0,
        storageUsed: 0
    }
}

export default {
    state: defaults(),
    mutations: {
        mediaPush (state: State, medias: Media[]) {
            state.media = window.removeDuplicatesById(state.media, medias)
        },
        mediaRemove (state: State, ids: number[]): void {
            //  Get our current lists of media
            const media = state.media

            //  If no media, do nothing
            if (!media) return

            //  Else, filter out our removed ids
            const filtered = media.filter((x: Media) => !ids.includes(x.id))

            //  Set our media to the filtered list
            state.media = filtered
        },
        mediaSet (state: State, medias: Media[]) {
            state.media = medias
        },
        storageLimitSet (state: State, limit: number) {
            state.storageLimit = limit
        },
        storageUsedSet (state: State, used: number) {
            state.storageUsed = used
        },
        mediaReset(state: State) {
            Object.assign(state, defaults())
        }
    },
    actions: {
        mediaDelete ({ commit, getters }, media: Media) {
            //  Grab our id from the supplied media
            const id = media.id

            //  Assemble the API endpoint
            const url = `${getters.api}/media/${id}`

            //  Send the request
            return window.axios.delete(url).then(response => {
                //  Remove from our global list
                commit('mediaRemove', [response.data.id])

                //  Remove from messages
                commit('messageMediaRemove', [response.data.id])

                //  Remove from media view
                commit('mediaViewMediaRemove', [response.data.id])

                //  Return our response
                return response.data
            }).catch(() => null)
        },
        mediaDownload ({ getters }, media: Media) {
            //  Grab our id from the supplied media
            const id = media.id

            //  Assemble the API endpoint
            const url = `${getters.api}/media/download/${id}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Create an anchor element
                const link = document.createElement('a');

                //  Set the href and download attr so we can download it
                link.href = response.data
                link.setAttribute('download', 'file.jpg');
                link.click();

                //  Return our response
                return response.data
            }).catch(() => null)
        },
        mediaFind ({ getters }, mediaId: number): Promise<Media> {
            //  Assemble the API endpoint
            const url = `${getters.api}/media/${mediaId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Return our found media
                return response.data
            }).catch(() => null)
        },
        mediaPost ({ commit, getters }, formData) {
            //  Assemble our API endpoint
            const url = `${getters.api}/media`

            //  Send the request
            return window.axios.post(url, formData).then(response => {
                //  Update our global list
                commit('mediaPush', [response.data])

                //  Update our message media list
                commit('messageMediaSync', [response.data])

                //  Update our media view media list
                commit('mediaViewMediaPush', [response.data])

                return response.data
            }).catch(() => null)
        },
        mediaPut ({ commit, getters }, media: Media) {
            //  Assemble our API endpoint
            const url = `${getters.api}/media`

            //  Send the request
            return window.axios.put(url, media).then(response => {
                //  Update our global list
                commit('mediaPush', [response.data])

                //  Update our message media list
                commit('messageMediaPush', [response.data])

                //  Update our media view media list
                commit('mediaViewMediaPush', [response.data])

                //  Return our updated media
                return response.data
            }).catch(() => null)
        },
        mediaSearch ({ getters }, payload: object): Promise<Media[]> {
            //  Assemble our API endpoint
            const url = `${getters.api}/media/search`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  just return our media we searched for
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        //  Our Media dataset
        media (state: State): Media[] {
            return state.media
        },
        storageLimit (state: State): number {
            return state.storageLimit
        },
        storageUsed (state: State): number {
            return state.storageUsed
        }
    }
}